// Apolo IDE — useStreamingChat hook.
//
// API:
//   const { send, streaming, error, abort, streamingMessage } = useStreamingChat(sessionId)
//   await send({ content })
//
// While streaming, `streamingMessage` carries the partial assistant text so
// the UI can render a typing-cursor experience. When the stream ends, the
// server-persisted message is reachable via the parent reloading
// /messages — this hook only owns the live partial.

import { useCallback, useEffect, useRef, useState } from 'react';
import { streamSSE } from '../lib/sseClient.js';
import { extractEditsBlocks } from '../lib/multiFileEdits.js';
import { useAgentStore } from '../store/useAgentStore.js';

// Liga/desliga o glow ao vivo da sessão na sidebar. getState() (não selector)
// pra não recriar callbacks nem re-renderizar este hook.
function markStreaming(sessionId, on) {
  if (!sessionId) return;
  try {
    useAgentStore.getState().setSessionStreaming(sessionId, on);
  } catch {
    // best-effort — nunca quebra o fluxo de chat
  }
}

export function useStreamingChat(sessionId, { onComplete } = {}) {
  const [streaming, setStreaming] = useState(false);
  const [streamingMessage, setStreamingMessage] = useState('');
  const [streamingThinking, setStreamingThinking] = useState('');
  const [error, setError] = useState(null);
  const abortRef = useRef(null);

  // No unmount / troca de sessão: SÓ aborta + apaga o glow se ESTE hook ainda
  // tem um stream em voo (abortRef set). Sem isso, navegar pra outra sessão
  // limpava `streamingSessionIds` de uma sessão que pode estar rodando por
  // OUTRO caminho (runAgent/setRunning) — e a bolinha verde sumia à toa.
  useEffect(
    () => () => {
      if (abortRef.current) {
        abortRef.current.abort();
        abortRef.current = null;
        markStreaming(sessionId, false);
      }
    },
    [sessionId],
  );

  const abort = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setStreaming(false);
    markStreaming(sessionId, false);
  }, [sessionId]);

  const send = useCallback(
    async ({ content, model }) => {
      if (!sessionId || !content?.trim()) return { ok: false, error: 'empty input' };
      setError(null);
      setStreaming(true);
      markStreaming(sessionId, true); // dot verde (running) na hora
      setStreamingMessage('');
      setStreamingThinking('');

      const controller = new AbortController();
      abortRef.current = controller;

      let assembled = '';
      let assembledThinking = '';
      const res = await streamSSE(`/api/code-ide/sessions/${sessionId}/chat/stream`, {
        body: { content, model },
        signal: controller.signal,
        onChunk(chunk) {
          if (chunk?.type === 'text' && typeof chunk.delta === 'string') {
            assembled += chunk.delta;
            setStreamingMessage(assembled);
          } else if (chunk?.type === 'thinking' && typeof chunk.delta === 'string') {
            assembledThinking += chunk.delta;
            setStreamingThinking(assembledThinking);
          }
          if (chunk?.type === 'done') {
            // stream end is also signaled via streamSSE return value
          }
        },
      });

      abortRef.current = null;
      setStreaming(false);
      markStreaming(sessionId, false); // fim do stream (sucesso ou erro) → apaga o verde

      if (!res.ok) {
        setError(res.error || 'falha de stream');
        return { ok: false, error: res.error };
      }

      // Sprint v11 E6 P4 — auto-detect XML <edits> blocks and surface to
      // the EditsTab. Lookup is regex-cheap, no-op when there's no block.
      try {
        const edits = extractEditsBlocks(assembled);
        if (edits.length > 0) {
          window.dispatchEvent(new CustomEvent('apolo:multiEdits', {
            detail: { edits, source: 'stream', sessionId },
          }));
        }
      } catch {
        // best effort — never break the chat flow
      }

      // Sprint v11 E15 P3 — hands-free reply playback. The hook ignores
      // the event when the loop is off, so dispatching unconditionally is safe.
      try {
        if (assembled && assembled.trim()) {
          window.dispatchEvent(new CustomEvent('apolo:handsFreeReply', {
            detail: { text: assembled },
          }));
        }
      } catch {
        // best effort
      }

      onComplete?.({ text: assembled, thinking: assembledThinking });
      setStreamingMessage('');
      setStreamingThinking('');
      return { ok: true, text: assembled, thinking: assembledThinking };
    },
    [sessionId, onComplete],
  );

  return { send, streaming, streamingMessage, streamingThinking, error, abort };
}
